import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  doc, getDoc, updateDoc, getDocs, collection, query, orderBy,
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, MenuItem, Divider, Grid, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LagbasLogo from '../components/LagbasLogo';
import { buildOrderConfirmationPdf, computeBudgetFromOffer } from '../utils/orderConfirmationPdf';

const STATUSES = [
  { key: 'draft',    label: 'Utkast',    color: '#546E7A', bg: '#ECEFF1' },
  { key: 'sent',     label: 'Skickad',   color: '#1565C0', bg: '#E3F2FD' },
  { key: 'signed',   label: 'Signerad',  color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'cancelled',label: 'Makulerad', color: '#B71C1C', bg: '#FFEBEE' },
];

const EMPTY = {
  status: 'draft',
  confirmationNumber: '',
  signingDate: new Date().toISOString().slice(0, 10),
  signingCity: 'Solna',
  projectId: '',
  offerId: '',
  offerNumber: '',
  supplierName: '',
  supplierOrgNumber: '',
  supplierAddress: '',
  supplierPhone: '',
  supplierEmail: '',
  supplierContactName: '',
  supplierContactPhone: '',
  supplierContactEmail: '',
  clientId: '',
  clientName: '',
  clientOrgNumber: '',
  clientAddress: '',
  clientZip: '',
  clientCity: '',
  clientPhone: '',
  clientContactName: '',
  clientContactPhone: '',
  clientContactEmail: '',
  clientInvoiceEmail: '',
  ourProjectName: '',
  clientProjectName: '',
  projectNumber: '',
  clientProjectNumber: '',
  projectAddress: '',
  invoiceReference: '',
  timeline: '',
  taskDescription: '',
  budgetHours: '',
  arvodesText: 'Ersättning enligt löpande räkning mot budget. Samtliga priser exklusive moms.',
  services: [],
  paymentTerms: '30 dagar netto',
  insurance: 'Konsulten har en giltig ansvarsförsäkring',
  standardTerms: 'ABK 09',
};

function SectionTitle({ children }) {
  return (
    <Box sx={{ mb: 2, pb: 1, borderBottom: '2px solid #FF6B00' }}>
      <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#FF6B00', textTransform: 'uppercase', letterSpacing: 1 }}>
        {children}
      </Typography>
    </Box>
  );
}

function Field({ label, children }) {
  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  );
}

export default function OrderConfirmationEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');

  // Picker data
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);

  useEffect(() => { loadAll(); }, [id]); // eslint-disable-line

  async function loadAll() {
    setLoading(true);
    try {
      const [confSnap, projSnap, usersSnap, crmSnap] = await Promise.all([
        getDoc(doc(db, 'orderConfirmations', id)),
        getDocs(query(collection(db, 'projects'), orderBy('name'))),
        getDocs(query(collection(db, 'users'), orderBy('name'))),
        getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
      ]);

      if (!confSnap.exists()) { navigate('/orderbekraftelser'); return; }

      setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCrmCompanies(crmSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const confData = confSnap.data();

      // First time: auto-fill empty confirmation from project/offer/company
      if (confData.projectId && !confData.supplierName) {
        const filled = await autoFillFromSources(confData);
        setForm({ ...EMPTY, ...confData, ...filled });
      } else {
        setForm({ ...EMPTY, ...confData });
        if (confData.clientId) loadContacts(confData.clientId);
      }
    } catch (err) {
      setError('Kunde inte ladda orderbekräftelsen.');
      console.error(err);
    }
    setLoading(false);
  }

  async function autoFillFromSources(confData) {
    const result = {};

    // Company (supplier)
    const companyId = userProfile?.companyId;
    if (companyId) {
      const compSnap = await getDoc(doc(db, 'companies', companyId));
      if (compSnap.exists()) {
        const c = compSnap.data();
        result.supplierName = c.name || '';
        result.supplierOrgNumber = c.orgNumber || '';
        result.supplierAddress = c.address || '';
        result.supplierPhone = c.phone || '';
        result.supplierEmail = c.email || '';
      }
    }

    // Project
    if (confData.projectId) {
      const projSnap = await getDoc(doc(db, 'projects', confData.projectId));
      if (projSnap.exists()) {
        const p = projSnap.data();
        result.ourProjectName = p.name || '';
        result.projectNumber = p.projectNumber || '';
        const timeline = [p.startDate, p.endDate].filter(Boolean).join(' – ');
        if (timeline) result.timeline = `Tas fram i samråd med projektledare. Start: ${p.startDate || ''}.`;
        if (p.description) result.taskDescription = p.description;

        // Offer
        const offerId = confData.offerId || p.offerId;
        if (offerId) {
          result.offerId = offerId;
          const offerSnap = await getDoc(doc(db, 'offers', offerId));
          if (offerSnap.exists()) {
            const o = offerSnap.data();
            result.offerNumber = o.offerNumber || '';
            const { hours, services } = computeBudgetFromOffer(o);
            if (hours) result.budgetHours = String(hours);
            if (services.length) result.services = services;
            // Client may come from offer if project lacks clientId
            if (!confData.clientId && o.companyId) {
              result.clientId = o.companyId;
              await fillClientFromCRM(o.companyId, result);
            }
          }
        }

        // Client from project
        const clientId = confData.clientId || p.clientId;
        if (clientId && !result.clientId) {
          result.clientId = clientId;
          await fillClientFromCRM(clientId, result);
        } else if (clientId && result.clientId === clientId) {
          await fillClientFromCRM(clientId, result);
        }
      }
    }

    return result;
  }

  async function fillClientFromCRM(clientId, result) {
    const crmSnap = await getDoc(doc(db, 'crmCompanies', clientId));
    if (!crmSnap.exists()) return;
    const c = crmSnap.data();
    result.clientName = c.name || '';
    result.clientOrgNumber = c.orgNumber || '';
    result.clientAddress = c.address || '';
    result.clientZip = c.zip || '';
    result.clientCity = c.city || '';
    result.clientPhone = c.phone || '';
    // Primary contact
    const contactsSnap = await getDocs(
      query(collection(db, 'crmCompanies', clientId, 'contacts'), orderBy('name'))
    );
    const contacts = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    setClientContacts(contacts);
    const primary = contacts.find(c => c.primary) || contacts[0];
    if (primary) {
      result.clientContactName = primary.name || '';
      result.clientContactEmail = primary.email || '';
      result.clientContactPhone = primary.phone || '';
      result.clientInvoiceEmail = primary.email || '';
    }
  }

  const loadContacts = useCallback(async (clientId) => {
    if (!clientId) { setClientContacts([]); return; }
    const snap = await getDocs(
      query(collection(db, 'crmCompanies', clientId, 'contacts'), orderBy('name'))
    );
    setClientContacts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, []);

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
    setSaved(false);
  }

  async function handleProjectChange(projectId) {
    set('projectId', projectId);
    if (!projectId) return;
    const proj = projects.find(p => p.id === projectId);
    if (!proj) return;
    const updates = {
      projectId,
      ourProjectName: proj.name || '',
      projectNumber: proj.projectNumber || '',
    };
    if (proj.description) updates.taskDescription = proj.description;

    const offerId = proj.offerId;
    if (offerId) {
      updates.offerId = offerId;
      const offerSnap = await getDoc(doc(db, 'offers', offerId));
      if (offerSnap.exists()) {
        const o = offerSnap.data();
        updates.offerNumber = o.offerNumber || '';
        const { hours, services } = computeBudgetFromOffer(o);
        if (hours) updates.budgetHours = String(hours);
        if (services.length) updates.services = services;
      }
    }

    if (proj.clientId) {
      updates.clientId = proj.clientId;
      await fillClientFromCRM(proj.clientId, updates);
    }

    setForm(f => ({ ...f, ...updates }));
    setSaved(false);
  }

  async function handleClientChange(clientId) {
    set('clientId', clientId);
    const crmCo = crmCompanies.find(c => c.id === clientId);
    if (crmCo) {
      setForm(f => ({
        ...f,
        clientId,
        clientName: crmCo.name || '',
        clientOrgNumber: crmCo.orgNumber || '',
        clientAddress: crmCo.address || '',
        clientZip: crmCo.zip || '',
        clientCity: crmCo.city || '',
        clientPhone: crmCo.phone || '',
      }));
    }
    await loadContacts(clientId);
    setSaved(false);
  }

  function handleContactChange(contactId) {
    const contact = clientContacts.find(c => c.id === contactId);
    if (!contact) return;
    setForm(f => ({
      ...f,
      clientContactName: contact.name || '',
      clientContactEmail: contact.email || '',
      clientContactPhone: contact.phone || '',
      clientInvoiceEmail: f.clientInvoiceEmail || contact.email || '',
    }));
    setSaved(false);
  }

  function handleOurContactChange(userId) {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setForm(f => ({
      ...f,
      supplierContactName: user.name || user.displayName || '',
      supplierContactEmail: user.email || '',
      supplierContactPhone: user.phone || user.mobile || '',
    }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, 'orderConfirmations', id), {
        ...form,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
      });
      setSaved(true);
    } catch (err) {
      setError('Kunde inte spara.');
      console.error(err);
    }
    setSaving(false);
  }

  async function handleDownloadPdf() {
    setPdfLoading(true);
    try {
      const companyId = userProfile?.companyId;
      let logoUrl = null;
      if (companyId) {
        const compSnap = await getDoc(doc(db, 'companies', companyId));
        logoUrl = compSnap.data()?.logo || null;
      }
      await buildOrderConfirmationPdf({ ...form, _logoUrl: logoUrl });
    } catch (err) {
      console.error(err);
    }
    setPdfLoading(false);
  }

  function addService() {
    setForm(f => ({ ...f, services: [...(f.services || []), { name: '', hourlyRate: '' }] }));
  }

  function updateService(i, field, val) {
    setForm(f => {
      const svs = [...(f.services || [])];
      svs[i] = { ...svs[i], [field]: val };
      return { ...f, services: svs };
    });
    setSaved(false);
  }

  function removeService(i) {
    setForm(f => ({ ...f, services: f.services.filter((_, idx) => idx !== i) }));
    setSaved(false);
  }

  const st = STATUSES.find(s => s.key === form.status) || STATUSES[0];

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate('/orderbekraftelser')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {form.confirmationNumber || 'Orderbekräftelse'}
              </Typography>
              <Chip label={st.label} size="small"
                sx={{ bgcolor: st.bg, color: st.color, fontWeight: 600, fontSize: '0.7rem' }} />
            </Box>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {form.ourProjectName || form.projectName || 'Inget uppdrag kopplat'}
              {form.clientName ? ` · ${form.clientName}` : ''}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {saved && <Typography variant="caption" sx={{ color: '#81C784' }}>Sparad</Typography>}
          <TextField select size="small" value={form.status} onChange={e => set('status', e.target.value)}
            sx={{ '& .MuiInputBase-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }, minWidth: 120 }}>
            {STATUSES.map(s => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
          </TextField>
          <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
            disabled={pdfLoading} onClick={handleDownloadPdf}
            sx={{ color: '#ff8a50', borderColor: '#ff8a50', textTransform: 'none', '&:hover': { bgcolor: 'rgba(255,107,0,0.15)' } }}>
            {pdfLoading ? 'Genererar…' : 'Ladda ned PDF'}
          </Button>
          <Button variant="contained" size="small" startIcon={<SaveIcon />}
            disabled={saving} onClick={handleSave}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, textTransform: 'none' }}>
            {saving ? 'Sparar…' : 'Spara'}
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>

          {/* ── KOPPLA UPPDRAG ───────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <SectionTitle>Kopplat uppdrag</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field label="Uppdrag (UppdragN)">
                    <TextField select fullWidth size="small" value={form.projectId}
                      onChange={e => handleProjectChange(e.target.value)}>
                      <MenuItem value="">– Välj uppdrag –</MenuItem>
                      {projects.map(p => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}{p.projectNumber ? ` (${p.projectNumber})` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Field>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Field label="Offertnummer">
                    <TextField fullWidth size="small" value={form.offerNumber}
                      onChange={e => set('offerNumber', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Field label="Bekräftelsenummer">
                    <TextField fullWidth size="small" value={form.confirmationNumber} disabled
                      sx={{ '& .MuiInputBase-input': { color: '#FF6B00', fontFamily: 'monospace', fontWeight: 700 } }} />
                  </Field>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ── LEVERANTÖR ──────────────────────────────────────────────── */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <SectionTitle>Leverantör (vår part)</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field label="Företagsnamn">
                    <TextField fullWidth size="small" value={form.supplierName}
                      onChange={e => set('supplierName', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Org.nummer">
                    <TextField fullWidth size="small" value={form.supplierOrgNumber}
                      onChange={e => set('supplierOrgNumber', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Telefon (växel)">
                    <TextField fullWidth size="small" value={form.supplierPhone}
                      onChange={e => set('supplierPhone', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Adress">
                    <TextField fullWidth size="small" value={form.supplierAddress}
                      onChange={e => set('supplierAddress', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="E-post (företag)">
                    <TextField fullWidth size="small" value={form.supplierEmail}
                      onChange={e => set('supplierEmail', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Kontaktperson</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Välj medarbetare">
                    <TextField select fullWidth size="small" value=""
                      onChange={e => handleOurContactChange(e.target.value)}>
                      <MenuItem value="">– Välj för att fylla i –</MenuItem>
                      {users.map(u => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name || u.displayName || u.email}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Namn">
                    <TextField fullWidth size="small" value={form.supplierContactName}
                      onChange={e => set('supplierContactName', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Mobil">
                    <TextField fullWidth size="small" value={form.supplierContactPhone}
                      onChange={e => set('supplierContactPhone', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="E-post">
                    <TextField fullWidth size="small" value={form.supplierContactEmail}
                      onChange={e => set('supplierContactEmail', e.target.value)} />
                  </Field>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ── BESTÄLLARE ──────────────────────────────────────────────── */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <SectionTitle>Beställare (kund)</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field label="Välj kund från CRM">
                    <TextField select fullWidth size="small" value={form.clientId}
                      onChange={e => handleClientChange(e.target.value)}>
                      <MenuItem value="">– Välj kund –</MenuItem>
                      {crmCompanies.map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </TextField>
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Företagsnamn">
                    <TextField fullWidth size="small" value={form.clientName}
                      onChange={e => set('clientName', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Org.nummer">
                    <TextField fullWidth size="small" value={form.clientOrgNumber}
                      onChange={e => set('clientOrgNumber', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Telefon (växel)">
                    <TextField fullWidth size="small" value={form.clientPhone}
                      onChange={e => set('clientPhone', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Adress">
                    <TextField fullWidth size="small" value={form.clientAddress}
                      onChange={e => set('clientAddress', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary">Kontaktperson</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Välj kontakt från kundkortet">
                    <TextField select fullWidth size="small" value=""
                      onChange={e => handleContactChange(e.target.value)}
                      disabled={!clientContacts.length}>
                      <MenuItem value="">– Välj kontakt –</MenuItem>
                      {clientContacts.map(c => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}{c.role ? ` – ${c.role}` : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Namn">
                    <TextField fullWidth size="small" value={form.clientContactName}
                      onChange={e => set('clientContactName', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="Mobil">
                    <TextField fullWidth size="small" value={form.clientContactPhone}
                      onChange={e => set('clientContactPhone', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6}>
                  <Field label="E-post">
                    <TextField fullWidth size="small" value={form.clientContactEmail}
                      onChange={e => set('clientContactEmail', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field label="Faktura e-post">
                    <TextField fullWidth size="small" value={form.clientInvoiceEmail}
                      onChange={e => set('clientInvoiceEmail', e.target.value)} />
                  </Field>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ── UPPDRAGSINFORMATION ─────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <SectionTitle>Uppdragsinformation</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field label="Projektnamn (vår benämning)">
                    <TextField fullWidth size="small" value={form.ourProjectName}
                      onChange={e => set('ourProjectName', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label="Projektnamn (kundens benämning)">
                    <TextField fullWidth size="small" value={form.clientProjectName}
                      onChange={e => set('clientProjectName', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Projektnummer (vår)">
                    <TextField fullWidth size="small" value={form.projectNumber}
                      onChange={e => set('projectNumber', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Projektnummer (kund)">
                    <TextField fullWidth size="small" value={form.clientProjectNumber}
                      onChange={e => set('clientProjectNumber', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label="Projektadress">
                    <TextField fullWidth size="small" value={form.projectAddress}
                      onChange={e => set('projectAddress', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label="Fakturamärkning/referens (kund)">
                    <TextField fullWidth size="small" value={form.invoiceReference}
                      onChange={e => set('invoiceReference', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field label="Tidplan">
                    <TextField fullWidth size="small" value={form.timeline}
                      onChange={e => set('timeline', e.target.value)}
                      placeholder="Tas fram i samråd med projektledare." />
                  </Field>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Ort (signatur)">
                    <TextField fullWidth size="small" value={form.signingCity}
                      onChange={e => set('signingCity', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Field label="Datum (signatur)">
                    <TextField fullWidth size="small" type="date" value={form.signingDate}
                      onChange={e => set('signingDate', e.target.value)} />
                  </Field>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ── UPPDRAGSBESKRIVNING ─────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <SectionTitle>Uppdragsbeskrivning</SectionTitle>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Inled varje punkt med "- " för att skapa en punktlista i PDF:en.
              </Typography>
              <TextField fullWidth multiline rows={6} size="small" value={form.taskDescription}
                onChange={e => set('taskDescription', e.target.value)}
                placeholder={'- Stöd till projektörer...\n- Rådgivning om best practices...\n- Avstämningsmöte (1 ggr/månad).'} />
            </Paper>
          </Grid>

          {/* ── BUDGET & ARVODE ─────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <SectionTitle>Budget &amp; Arvode</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Field label="Uppskattad budget (timmar)">
                    <TextField fullWidth size="small" value={form.budgetHours}
                      onChange={e => set('budgetHours', e.target.value)}
                      placeholder="40" />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Field label="Arvodestext">
                    <TextField fullWidth size="small" value={form.arvodesText}
                      onChange={e => set('arvodesText', e.target.value)} />
                  </Field>
                </Grid>
              </Grid>

              {/* Services table */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    SPECIFIKATION / TJÄNSTER
                  </Typography>
                  <Tooltip title="Fylls i automatiskt från offertens budget">
                    <AutoFixHighIcon sx={{ fontSize: 16, color: '#aaa' }} />
                  </Tooltip>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1a1a1a' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 700 }}>Specifikation / Tjänst</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, width: 160 }}>Timpris SEK/h</TableCell>
                      <TableCell sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(form.services || []).map((svc, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <TextField fullWidth size="small" value={svc.name}
                            onChange={e => updateService(i, 'name', e.target.value)}
                            placeholder="Rådgivande stödresurs" />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={svc.hourlyRate}
                            onChange={e => updateService(i, 'hourlyRate', e.target.value)}
                            placeholder="1250" type="number"
                            sx={{ width: 140 }} />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeService(i)}>
                            <DeleteIcon fontSize="small" sx={{ color: '#ccc' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Button size="small" startIcon={<AddIcon />} onClick={addService}
                          sx={{ textTransform: 'none', color: '#FF6B00' }}>
                          Lägg till rad
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Grid>

          {/* ── VILLKOR ─────────────────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <SectionTitle>Villkor &amp; Generellt</SectionTitle>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Field label="Betalningsvillkor">
                    <TextField fullWidth size="small" value={form.paymentTerms}
                      onChange={e => set('paymentTerms', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field label="Generella villkor (standard)">
                    <TextField fullWidth size="small" value={form.standardTerms}
                      onChange={e => set('standardTerms', e.target.value)} />
                  </Field>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Field label="Försäkring">
                    <TextField fullWidth size="small" value={form.insurance}
                      onChange={e => set('insurance', e.target.value)} />
                  </Field>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

        </Grid>

        {/* Bottom save bar */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />}
            disabled={pdfLoading} onClick={handleDownloadPdf}
            sx={{ textTransform: 'none', color: '#B71C1C', borderColor: '#B71C1C' }}>
            {pdfLoading ? 'Genererar…' : 'Ladda ned PDF'}
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />}
            disabled={saving} onClick={handleSave}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, textTransform: 'none' }}>
            {saving ? 'Sparar…' : 'Spara ändringar'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
